<template>
    <LayoutBox class="notification-form">
        <header class="notification-form__header">
            <h4>
                {{ title }}
            </h4>

            <a
				class="notification-form__btn-preview"
				@click.prevent="showPreview = true"
			>
                Preview Post
            </a>
        </header>

		<form @submit.prevent="submit">
			<div class="notification-form__row">
				<AppTextField
					maxlength="50"
					v-model="notification.title"
					:required="true"
				>
					Notification Title
				</AppTextField>
			</div>

			<div class="notification-form__row">
				<AppTextarea
					maxlength="50"
					v-model="notification.description"
					:required="true"
				>
					Notification Description
				</AppTextarea>
			</div>

			<div class="notification-form__row">
				<div class="notification-form__col">
					<AppDatePicker
						:value="notification.date ? new Date(notification.date) : null"
						@input="notification.date = $event"
						placeholder="Select Date"
						:min="new Date()"
						:required="true"
					>
						Date
					</AppDatePicker>
				</div>

				<div class="notification-form__col">
					<AppTimePicker
						:value="notification.time ? $options.filters.time(notification.time, 'hh:mm') : null"
						@input="inputTime"
						placeholder="Select time"
						:min="minTime"
						:required="true"
					>
						Time
					</AppTimePicker>
				</div>
			</div>

			<div class="notification-form__row">
				<div class="notification-form__col">
					<AppDropdown
						v-model="notification.country"
						:options="countryList"
                        display-prop="name"
						placeholder="Select country"
						:searchable="true"
						:multiple="true"
						:loading="countryListLoading"
						@input="resetUserList"
					>
						Country
					</AppDropdown>
				</div>
			</div>

			<div class="notification-form__row">
				<div class="notification-form__col">
					<div class="notification-form__col">
						<AppNumberField
							placeholder="from"
							v-model="notification.ageFrom"
							@input="setUserAge"
						>
							Age
						</AppNumberField>

						<AppNumberField
							placeholder="to"
							v-model="notification.ageTo"
							@input="setUserAge"
							:disabled="!parseInt(notification.ageFrom)"
							:min="notification.ageFrom || null"
						>
							<span style="visibility:hidden;">to</span>
						</AppNumberField>
					</div>

					<AppDropdown
						v-model="notification.gender"
						:options="['Male', 'Female', 'Others']"
						placeholder="Select Gender"
						:multiple="true"
						@input="resetUserList"
					>
						Sex
					</AppDropdown>
				</div>
			</div>

			<div class="notification-form__row">
				<div class="notification-form__col">
					<AppDropdown
						display-prop="name"
						placeholder="Select user"
						:multiple="true"
						:searchable="true"
						v-model="notification.users"
						:options="userList.data"
						:loading="userListLoading"
						@search="searchUser"
						@end-of-scroll="loadMoreUserList"
					>
						Select User
					</AppDropdown>
				</div>
			</div>

			<footer class="notification-form__controls">
				<AppButton
					modifier="small-danger"
					@click="$emit('cancel')"
                    type="button"
                    :disabled="buttonLoading"
				>
					Cancel
				</AppButton>

				<AppButton
					type="submit"
					modifier="small-primary"
					:disabled="buttonLoading"
				>
					Post
				</AppButton>
			</footer>
		</form>

		<div
			v-if="buttonLoading || countryListLoading"
			class="notification-form__loading"
		>
			<AppLoading/>
		</div>

        <NotificationPreview
            v-if="showPreview"
            @close="showPreview = false"
            :notification="notification"
        ></NotificationPreview>

		<AppPopup
			:visible="modalName.includes('not-empty-form')"
			type="warning"
			title="Warning"
			:message="modalMessage"
			@close="$store.dispatch('closeModal')"
		>
			<AppButton @click="$store.dispatch('confirm')" modifier="small-primary">Ok</AppButton>
			<AppButton @click="$store.dispatch('closeModal')" modifier="small-danger">Cancel</AppButton>
		</AppPopup>
    </LayoutBox>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import NotificationPreview from './NotificationPreview.vue'

export default {
	props: {
		notificationId: {
			type: String,
			default: null
		},

		title: {
			type: String,
			required: true
		}
	},

	created () {
		this.getUserList()
	},

	data () {
		return {
			showPreview: false,
			URL: process.env.VUE_APP_BASE
		}
	},

	computed: {
		...mapFields('notification', {
			notification: 'notification',
			oldNotification: 'oldNotification',
			errors: 'errors',
			buttonLoading: 'buttonLoading',
			countryList: 'countryList',
			countryListLoading: 'countryListLoading',
			userList: 'userList',
			userListLoading: 'userListLoading',
			userFilter: 'userFilter',
			countryKeyword: 'countryKeyword'
		}),

		...mapGetters({
			modalName: 'modalName'
		}),

		hasFilters () {
			if (this.notification.users.length > 0 ||
                this.userFilter.country ||
                this.userFilter.age ||
                this.userFilter.gender
			) return true

			return false
		},

		modalMessage () {
			const messages = {
				'edit-notification': 'Your changes will be lost.',
				'create-notification': 'Your input will be lost.'
			}

			return messages[this.$route.name]
		},

		minTime () {
			if (this.notification.date &&
				new Date(this.notification.date).toDateString() === new Date().toDateString()) {
				const timestamp = new Date().getTime() + 60000
				return this.$options.filters.time(new Date(timestamp), 'hh:mm')
			}

			return null
		}
	},

	watch: {
		'notification.ageFrom' (value) {
			if (this.notification.ageTo && this.notification.ageTo < value) this.notification.ageTo = null
		}
	},

	methods: {
		inputTime (time) {
			if (time) {
				this.notification.time = new Date(`${new Date(this.notification.date).toDateString()} ${time}`)
			} else {
				this.notification.time = null
			}
		},

		removeItem (index, key) {
			this.notification[key].splice(index, 1)

			if (['country', 'gender'].includes(key)) {
				this.resetUserList()
			}
		},

		resetUserList () {
			this.userList = {
				current_page: 0,
				last_page: 0,
				data: []
			}

			this.getUserList()
		},

		async setUserAge (e) {
			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				if (parseInt(this.notification.ageFrom)) {
					this.resetUserList()
				}
			}, 500)
		},

		async searchUser (e) {
			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.userFilter.keyword = e
				this.userList = {
					data: [],
					current_page: 0
				}
				this.getUserList()
			}, 500)
		},

		loadMoreUserList () {
			const { current_page, last_page } = this.userList

			if (current_page === last_page && this.userListLoading) return

			this.getUserList()
		},

		async getUserList () {
			const {
				country,
				ageTo,
				ageFrom,
				gender
			} = this.notification
			let apiQuery = `?page=${++this.userList.current_page}`

			if (this.userFilter.keyword) {
				apiQuery += `&keyword=${this.userFilter.keyword}`
			}

			if (gender.length) {
				apiQuery += `&gender=${gender.join()}`
			}

			if (country.length) {
				apiQuery += `&country=${country.map(item => item.name).join()}`
			}

			if (ageFrom && ageFrom <= 100) {
				if (parseInt(ageTo) && parseInt(ageFrom) <= parseInt(ageTo) && parseInt(ageTo) <= 100) {
					apiQuery += `&age_from=${ageFrom}`
					apiQuery += `&age_to=${ageTo}`
				} else if (!parseInt(ageTo)) {
					apiQuery += `&age_from=${ageFrom}`
					apiQuery += `&age_to=${ageFrom}`
				}
			}

			this.$store.dispatch('notification/getUserList', apiQuery)
		},

		async getCountryList () {
			this.$store.dispatch('notification/getCountryList')
		},

		clearFilters () {
			this.userFilter = {
				country: false,
				age: false,
				gender: false,
				keyword: ''
			}
		},

		async submit () {
			const form = await this.getForm()
			this.$emit('post', form)
		},

		async getForm () {
			const {
				ageTo,
				ageFrom,
				title,
				description,
				date,
				time,
				country,
				gender,
				users
			} = this.notification

			const { filters } = this.$options
			const timeFormatted = filters.dateFormatter(time, 'HH:mm:ss')
			const dateTime = `${filters.dateFormatter(date, 'YYYY-MM-DD')} ${timeFormatted}`
			const form = new FormData()

			form.append('title', title)
			form.append('description', description)
			form.append('notification_date', dateTime)

			if (users.length) {
				form.append('user_id', users.map(item => item.id).join())
			}

			if (ageFrom) {
				form.append('age_from', ageFrom)

				if (ageTo) {
					form.append('age_to', ageTo)
				} else {
					form.append('age_to', ageFrom)
				}
			}

			if (gender.length) {
				form.append('gender', gender)
			}

			if (country.length) {
				form.append('country', country.map(item => item.name).join())
			}

			return form
		}
	},

	components: {
		NotificationPreview
	}
}
</script>

<style lang="scss" scoped>
.notification-form {
    display: inline-block;
    background-color: #fff;
	width: 40em;
	position: relative;

	&__loading {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(255,255,255,.5);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__header {
		padding-bottom: 1em;
		border-bottom: 1px solid lighten($default-light, 12%);
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 1em;
	}

	&__btn-preview {
		color: $primary;
		cursor: pointer;
	}

	&__row {
		display: flex;
		align-items: flex-start;
		margin-bottom: 1em;

		> *:last-child { flex: 1 1 auto; }
	}

	&__col {
		max-width: 55%;
		display: flex;

		&:first-child {
			width: 55%;
		}

		&:not(:first-child),
		> *:not(:first-child) {
			padding-left: 1em;
		}

		> * {
			flex: 1 1 auto;
		}
	}

	&__controls {
		margin-top: 4em;
		display: flex;
		justify-content: space-between;
	}
}
</style>
